import { useEffect } from "react";

/* Infinite animations that run off screen are pure waste — the sun, the
 * floating devices, the pulsing halos and the scroll cue were all still
 * spending frames while nowhere near the viewport.
 *
 * One observer marks every <section> that has left the viewport with
 * `.is-idle`; the stylesheets pause their own animations on that class
 * (see sun.css, lynqu.css, flatrent.css). */
export function useOffscreenAnimationPause() {
  useEffect(() => {
    const sections = Array.from(document.querySelectorAll("section"));
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-idle", !entry.isIntersecting);
        });
      },
      // A margin either side so nothing resumes mid-frame as it slides in.
      { rootMargin: "20% 0px 20% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);
}

/* Scroll-snap is only wanted when the sections are actually about one
 * viewport tall; centring a section twice the height of the screen is worse
 * than not snapping at all.
 *
 * The answer is ONE attribute on <html> that CSS reads, rather than a `sec`
 * class written onto eleven <section> elements — which is what used to get
 * baked into the prerendered HTML at the snapshot's desktop measurements and
 * then applied to every phone that loaded the page. */
export function useScrollSnap() {
  useEffect(() => {
    const root = document.documentElement;
    let frame = null;

    const evaluate = () => {
      frame = null;
      const sections = Array.from(document.querySelectorAll("section"));
      if (!sections.length) return;
      const viewport = window.innerHeight;
      // The last section (the footer) is never full height by design.
      const uniform = sections
        .slice(0, -1)
        .every((s) => Math.abs(viewport - s.clientHeight) <= 100);
      root.dataset.snap = uniform ? "on" : "off";
    };

    const schedule = () => {
      if (frame == null) frame = requestAnimationFrame(evaluate);
    };

    schedule();
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("resize", schedule);
      if (frame != null) cancelAnimationFrame(frame);
      delete root.dataset.snap;
    };
  }, []);
}
